<template>
  <div class="field-editor">
    <!-- Empty State -->
    <div v-if="!field" class="empty-state">
      <div class="empty-icon">
        <i class="bx bx-pointer"></i>
      </div>
      <h5>Propriedades do Campo</h5>
      <p>Selecione um campo para editar suas propriedades</p>
    </div>

    <!-- Field Properties -->
    <template v-else>
      <div class="editor-header">
        <h5>
          Propriedades do Campo
          <span v-if="isStandardField" class="badge badge-warning ml-2" style="font-size: 10px; vertical-align: middle;">
            Padrão
          </span>
          <span v-if="isFormField" class="badge badge-info ml-2" style="font-size: 10px; vertical-align: middle;">
            Formulário
          </span>
          <span v-if="isInternalField" class="badge badge-secondary ml-2" style="font-size: 10px; vertical-align: middle;">
            Interno
          </span>
          <span v-if="isRegistrationControlField" class="badge ml-2" style="font-size: 10px; vertical-align: middle; background: #fdebd0; color: #e67e22;">
            Ctrl. Inscrição
          </span>
        </h5>
        <span class="field-type-label">{{ getFieldTypeLabel() }}</span>
      </div>

      <div class="editor-content">
        <!-- RC Info Box -->
        <div v-if="isRegistrationControlField" class="rc-info-box">
          <i class="bx bx-info-circle"></i>
          <span>Este campo é preenchido automaticamente com dados do Controle de Inscrição e será somente leitura.</span>
        </div>

        <!-- Basic Settings Section -->
        <div class="settings-section">
          <div class="section-title">Configurações Básicas</div>

          <div class="form-group">
            <label>Rótulo do Campo</label>
            <input
              v-model="localField.label"
              type="text"
              class="form-control"
              placeholder="Digite o rótulo"
              @input="emitUpdate"
            />
          </div>

          <div v-if="!isStandardField && !isRegistrationControlField" class="form-group">
            <label>Placeholder</label>
            <input
              v-model="localField.placeholder"
              type="text"
              class="form-control"
              placeholder="Texto de exemplo..."
              @input="emitUpdate"
            />
          </div>

          <div class="form-group">
            <label>Texto de Ajuda (Opcional)</label>
            <input
              v-model="localField.help_text"
              type="text"
              class="form-control"
              placeholder="Adicione uma dica para o usuário..."
              @input="emitUpdate"
            />
          </div>

          <div v-if="!isStandardField && !isRegistrationControlField" class="form-group toggle-group">
            <label>Campo Obrigatório</label>
            <b-form-checkbox
              v-model="localField.is_required"
              switch
              @change="emitUpdate"
            />
          </div>
        </div>

        <!-- Visibility Section -->
        <div v-if="!isRegistrationControlField" class="settings-section">
          <div class="section-title">Visibilidade</div>

          <div class="form-group toggle-group">
            <label>
              Campo Visível
              <small class="text-muted d-block" style="font-weight: normal; margin-top: 4px;">
                Quando desativado, o campo não aparece na ficha
              </small>
            </label>
            <b-form-checkbox
              v-model="localField.is_visible"
              switch
              @change="emitUpdate"
            />
          </div>

          <div class="form-group">
            <label>Regras de Visibilidade</label>
            <select
              v-model="visibilityMode"
              class="form-control"
              @change="onVisibilityModeChange"
            >
              <option value="all">Visível para todos</option>
              <option value="groups">Visível apenas para grupos específicos</option>
              <option value="conditional">Condicional (baseado em outros campos)</option>
            </select>
          </div>

          <div v-if="visibilityMode === 'groups'" class="form-group">
            <label>Grupos Permitidos</label>
            <div class="groups-checkboxes">
              <div
                v-for="group in groups"
                :key="group.id"
                class="custom-control custom-checkbox"
              >
                <input
                  :id="'group-' + group.id"
                  v-model="selectedGroupIds"
                  type="checkbox"
                  class="custom-control-input"
                  :value="group.id"
                  @change="onGroupsChange"
                />
                <label class="custom-control-label" :for="'group-' + group.id">
                  {{ group.name }}
                </label>
              </div>
            </div>
          </div>

          <div v-if="visibilityMode === 'conditional'" class="conditions-section">
            <div v-if="conditions.length === 0" class="conditions-empty">
              <i class="bx bx-info-circle"></i>
              <span>Nenhuma condição adicionada. O campo será sempre visível.</span>
            </div>

            <div
              v-for="(condition, idx) in conditions"
              :key="idx"
              class="condition-row"
            >
              <div class="condition-fields">
                <div class="condition-field">
                  <label>Campo</label>
                  <select
                    v-model="condition.field"
                    class="form-control"
                    @change="updateConditions"
                  >
                    <option value="">Selecione...</option>
                    <option
                      v-for="f in availableConditionFields"
                      :key="f.key"
                      :value="f.key"
                    >{{ f.label }}</option>
                  </select>
                </div>
                <div class="condition-operator">
                  <label>Operador</label>
                  <select
                    v-model="condition.operator"
                    class="form-control"
                    @change="updateConditions"
                  >
                    <option
                      v-for="op in operatorOptions"
                      :key="op.value"
                      :value="op.value"
                    >{{ op.label }}</option>
                  </select>
                </div>
                <div
                  v-if="condition.operator !== 'not_empty' && condition.operator !== 'empty'"
                  class="condition-value"
                >
                  <label>Valor</label>
                  <input
                    v-model="condition.value"
                    type="text"
                    class="form-control"
                    placeholder="Valor..."
                    @input="updateConditions"
                  />
                </div>
              </div>
              <button
                class="btn-remove-condition"
                title="Remover condição"
                @click="removeCondition(idx)"
              >
                <i class="bx bx-trash"></i>
              </button>
            </div>

            <button class="btn-add-condition" @click="addCondition">
              <i class="bx bx-plus"></i>
              Adicionar condição
            </button>

            <div v-if="conditions.length > 1" class="conditions-logic-hint">
              <i class="bx bx-info-circle"></i>
              Todas as condições devem ser verdadeiras (lógica AND)
            </div>
          </div>
        </div>

        <!-- Advanced Settings Section -->
        <div class="settings-section">
          <div class="section-title collapsible" @click="showAdvanced = !showAdvanced">
            <span>Configurações Avançadas</span>
            <i :class="showAdvanced ? 'bx bx-chevron-up' : 'bx bx-chevron-down'"></i>
          </div>

          <div v-if="showAdvanced" class="advanced-content">
            <div class="form-group">
              <label>Largura</label>
              <select
                v-model="localLayout.cols"
                class="form-control"
                @change="updateLayout"
              >
                <option value="12">Largura total (100%)</option>
                <option value="6">Meia largura (50%)</option>
                <option value="4">Um terço (33%)</option>
                <option value="3">Um quarto (25%)</option>
                <option value="8">Dois terços (66%)</option>
                <option value="9">Três quartos (75%)</option>
              </select>
            </div>

            <div v-if="!isRegistrationControlField" class="form-group toggle-group">
              <label>Somente Leitura</label>
              <b-form-checkbox
                v-model="localField.is_readonly"
                switch
                @change="emitUpdate"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'FieldEditor',
  props: {
    field: {
      type: Object,
      default: null
    },
    groups: {
      type: Array,
      default: function () { return [] }
    },
    allFields: {
      type: Array,
      default: function () { return [] }
    }
  },
  data () {
    return {
      localField: {},
      localLayout: { cols: '12' },
      visibilityMode: 'all',
      selectedGroupIds: [],
      showAdvanced: false,
      currentFieldId: null,
      conditions: []
    }
  },
  computed: {
    isStandardField () {
      return this.localField.field_source === 'standard'
    },
    isFormField () {
      return this.localField.field_source === 'form_field'
    },
    isInternalField () {
      return this.localField.field_source === 'internal'
    },
    isRegistrationControlField () {
      return this.localField.field_source === 'registration_control' ||
        (this.localField.field_config && this.localField.field_config.source === 'registration_control')
    },
    availableConditionFields () {
      var self = this
      var currentId = this.localField.id
      return this.allFields
        .filter(function (f) { return f.id !== currentId })
        .map(function (f) {
          return {
            key: self.getFieldKey(f),
            label: self.getFieldLabel(f)
          }
        })
    },
    operatorOptions () {
      return [
        { value: 'equals', label: 'Igual a' },
        { value: 'not_equals', label: 'Diferente de' },
        { value: 'contains', label: 'Contém' },
        { value: 'not_empty', label: 'Está preenchido' },
        { value: 'empty', label: 'Está vazio' }
      ]
    }
  },
  watch: {
    field: {
      immediate: true,
      handler (val) {
        // Só resetar se o campo mudou (ID diferente) ou se val é null
        if (val) {
          if (this.currentFieldId !== val.id) {
            this.currentFieldId = val.id
            this.localField = Object.assign({}, val)
            this.localLayout = val.layout ? Object.assign({}, val.layout) : { cols: '12' }

            // Para campos internos, popular label e name a partir de field_config
            if (val.field_source === 'internal' && val.field_config) {
              this.localField.label = val.field_config.label || val.label || ''
              this.localField.name = val.field_config.name || ''
              this.localField.placeholder = val.field_config.placeholder || val.placeholder || ''
              this.localField.help_text = val.field_config.help_text || val.help_text || ''
            }

            this.loadVisibilitySettings()
          }
        } else {
          this.currentFieldId = null
          this.localField = {}
          this.localLayout = { cols: '12' }
          this.visibilityMode = 'all'
          this.selectedGroupIds = []
        }
      }
    }
  },
  methods: {
    getFieldTypeLabel () {
      if (this.isStandardField) {
        var labels = {
          name: 'Nome Completo',
          badge_name: 'Nome para Crachá',
          email: 'E-mail',
          cpf: 'CPF',
          phone: 'Telefone',
          gender: 'Gênero',
          birth_date: 'Data de Nascimento',
          address: 'Endereço',
          city: 'Cidade',
          state: 'Estado',
          zip_code: 'CEP',
          country: 'País'
        }
        return labels[this.localField.standard_field_name] || 'Campo Padrão'
      }

      if (this.isFormField && this.localField.form_field && this.localField.form_field.field_type) {
        return this.localField.form_field.field_type.label || 'Campo de Formulário'
      }

      if (this.isRegistrationControlField) {
        return 'Controle de Inscrição'
      }

      if (this.isInternalField) {
        // Check field_type in both field_config and directly
        var fieldTypeLabels = {
          text: 'Texto',
          textarea: 'Área de Texto',
          number: 'Número',
          select: 'Lista Suspensa',
          multiselect: 'Seleção Múltipla',
          checkbox: 'Checkbox',
          radio: 'Radio',
          date: 'Data',
          datetime: 'Data e Hora',
          toggle: 'Toggle'
        }
        var fieldTypeName = null
        if (this.localField.field_config && this.localField.field_config.field_type) {
          fieldTypeName = this.localField.field_config.field_type
        } else if (this.localField.field_type) {
          fieldTypeName = typeof this.localField.field_type === 'string'
            ? this.localField.field_type
            : this.localField.field_type.name
        }
        return fieldTypeLabels[fieldTypeName] || 'Campo Interno'
      }

      return 'Campo'
    },

    loadVisibilitySettings () {
      if (!this.localField.visibility_rules) {
        this.visibilityMode = 'all'
        this.selectedGroupIds = []
        this.conditions = []
        return
      }

      var rules = this.localField.visibility_rules
      this.visibilityMode = rules.mode || 'all'

      if (this.visibilityMode === 'groups' && rules.groups) {
        this.selectedGroupIds = rules.groups || []
        this.conditions = []
      } else if (this.visibilityMode === 'conditional' && rules.conditions) {
        this.conditions = rules.conditions.map(function (c) {
          return { field: c.field || '', operator: c.operator || 'equals', value: c.value || '' }
        })
        this.selectedGroupIds = []
      } else {
        this.selectedGroupIds = []
        this.conditions = []
      }
    },

    onVisibilityModeChange () {
      if (this.visibilityMode === 'all') {
        this.localField.visibility_rules = null
        this.selectedGroupIds = []
        this.conditions = []
      } else if (this.visibilityMode === 'groups') {
        this.localField.visibility_rules = {
          mode: 'groups',
          groups: []
        }
        this.selectedGroupIds = []
        this.conditions = []
      } else if (this.visibilityMode === 'conditional') {
        this.conditions = []
        this.selectedGroupIds = []
        this.localField.visibility_rules = {
          mode: 'conditional',
          conditions: []
        }
      }
      this.emitUpdate()
    },

    onGroupsChange () {
      if (!this.localField.visibility_rules) {
        this.localField.visibility_rules = { mode: 'groups', groups: [] }
      }
      this.localField.visibility_rules.groups = this.selectedGroupIds
      this.emitUpdate()
    },

    updateLayout () {
      this.localField.layout = Object.assign({}, this.localLayout)
      this.emitUpdate()
    },

    getFieldKey (field) {
      if (field.field_source === 'standard') {
        return field.standard_field_name
      }
      if (field.field_source === 'form_field' && field.form_field) {
        return field.form_field.name || ('field_' + field.form_field.id)
      }
      if (field.field_source === 'internal') {
        return (field.field_config && field.field_config.name) || ('internal_' + field.id)
      }
      if (field.field_source === 'registration_control' && field.field_config) {
        return field.field_config.name || field.field_config.rc_field || ('rc_' + field.id)
      }
      return 'field_' + field.id
    },

    getFieldLabel (field) {
      if (field.label) return field.label
      if (field.field_config && field.field_config.label) return field.field_config.label
      if (field.form_field && field.form_field.label) return field.form_field.label
      return this.getFieldKey(field)
    },

    addCondition () {
      this.conditions.push({ field: '', operator: 'equals', value: '' })
      this.updateConditions()
    },

    removeCondition (index) {
      this.conditions.splice(index, 1)
      this.updateConditions()
    },

    updateConditions () {
      this.localField.visibility_rules = {
        mode: 'conditional',
        conditions: this.conditions.map(function (c) {
          return { field: c.field, operator: c.operator, value: c.value }
        })
      }
      this.emitUpdate()
    },

    emitUpdate () {
      this.$emit('change', Object.assign({}, this.localField))
    }
  }
}
</script>

<style scoped>
.field-editor {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
  text-align: center;
}

.empty-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-icon i {
  font-size: 24px;
  color: #9ca3af;
}

.empty-state h5 {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.empty-state p {
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
}

/* Editor Header */
.editor-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
}

.editor-header h5 {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 4px 0;
}

.field-type-label {
  font-size: 12px;
  color: #6b7280;
}

/* Editor Content */
.editor-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.editor-content::-webkit-scrollbar {
  width: 6px;
}

.editor-content::-webkit-scrollbar-track {
  background: transparent;
}

.editor-content::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

.editor-content::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}

/* Settings Sections */
.settings-section {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f3f4f6;
}

.settings-section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
}

.section-title.collapsible {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  margin-bottom: 0;
  padding: 8px 0;
}

.section-title.collapsible:hover {
  color: #556ee6;
}

/* Form Groups */
.form-group {
  margin-bottom: 16px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.form-group .form-control {
  font-size: 13px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.form-group .form-control:focus {
  border-color: #556ee6;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
}

.form-group small {
  display: block;
  margin-top: 4px;
  font-size: 11px;
}

.toggle-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.toggle-group label {
  margin-bottom: 0;
}

/* Groups Checkboxes */
.groups-checkboxes {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
}

.groups-checkboxes .custom-control {
  margin-bottom: 0;
}

.groups-checkboxes .custom-control-label {
  font-size: 13px;
  font-weight: normal;
  color: #374151;
}

/* Advanced Content */
.advanced-content {
  margin-top: 16px;
}

/* RC Info Box */
.rc-info-box {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 16px;
  background: #fef9f0;
  border: 1px solid #fdebd0;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 12px;
  color: #b45309;
  line-height: 1.4;
}

.rc-info-box i {
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 1px;
}

/* Conditions Section */
.conditions-section {
  margin-top: 8px;
}

.conditions-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #f0f4ff;
  border-radius: 6px;
  font-size: 12px;
  color: #556ee6;
  margin-bottom: 12px;
}

.conditions-empty i {
  font-size: 16px;
  flex-shrink: 0;
}

.condition-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 8px;
}

.condition-fields {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.condition-field,
.condition-operator,
.condition-value {
  width: 100%;
}

.condition-fields label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 4px;
}

.condition-fields .form-control {
  font-size: 12px;
  padding: 6px 10px;
  height: auto;
}

.btn-remove-condition {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #ef4444;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  margin-top: 20px;
  transition: all 0.15s;
}

.btn-remove-condition:hover {
  background: #fef2f2;
}

.btn-add-condition {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px dashed #d0d5dd;
  background: transparent;
  border-radius: 6px;
  font-size: 12px;
  color: #556ee6;
  cursor: pointer;
  width: 100%;
  justify-content: center;
  transition: all 0.15s;
  margin-top: 4px;
}

.btn-add-condition:hover {
  border-color: #556ee6;
  background: #f0f4ff;
}

.conditions-logic-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 11px;
  color: #6b7280;
}

.conditions-logic-hint i {
  font-size: 14px;
  flex-shrink: 0;
}
</style>
